import type { CustomSSOType } from './api/v1/obt/common/auth_config';
import { AuthConfigAuthProviderAuthProviderType as AuthProviderType } from './api/v1/obt/common/auth_config';
import type {
  AuthenticatedUserBasicInfo,
  AuthenticatedUserBasicInfoExistingUser,
  ImpersonateUserResponse,
} from './api/v1/auth/services';
import type { RedirectInfo } from './api/v2/obt/model/redirect-info';
import type { ProcessApprovalRequest, ApprovalResponse } from './api/v1/obt/common/approval';
import { ApprovalType } from './api/v1/obt/common/approval';
import type { PersonaEnum } from './api/v1/obt/common/user_org_id';

export type PasswordStrength = {
  lower: boolean;
  upper: boolean;
  number: boolean;
  special: boolean;
  length: boolean;
};

export type AmplifyError = Error & {
  code: string;
};

export enum AuthProviderKeys {
  SIGN_IN = 'signIn',
  SIGN_UP = 'signUp',
  GOOGLE = 'Google',

  REDIRECT = 'redirect',
  EMAIL_OTP = 'emailOtp',
}

export type AuthProviderInfo =
  | { type: AuthProviderType.AZURE; key: string }
  | { type: AuthProviderType.CUSTOM; key: string; ssoType: CustomSSOType }
  | { type: AuthProviderType.GOOGLE; key: AuthProviderKeys.GOOGLE }
  | { type: AuthProviderType.USER_CREDENTIAL; key: AuthProviderKeys.SIGN_IN | AuthProviderKeys.SIGN_UP }
  | { type: AuthProviderType.REDIRECT; redirectUrl: string; key: AuthProviderKeys.REDIRECT }
  | { type: AuthProviderType.OTP; key: AuthProviderKeys.EMAIL_OTP }
  | { type: AuthProviderType.AUTH_CODE; redirectUrl: string; key: AuthProviderKeys.REDIRECT };

export type AuthMultiAppProviderInfo = AuthProviderInfo & {
  applicationId: string;
  applicationName: string;
  tmcId: string;
  orgId: string;
  persona: PersonaEnum;
  orgName: string;
  availableAuthProviders: AuthProviderInfo[];
};

export { AuthProviderType };

export type IAuthenticatedUserBasicInfo = AuthenticatedUserBasicInfo;
export type IRedirectInfo = RedirectInfo;

export type IAuthenticatedUserBasicInfoExistingUser = AuthenticatedUserBasicInfoExistingUser;

export enum CodeVerificationError {
  ExpiredCodeException,
  MismatchCodeException,
  UnknownError,
  NoError,
}

export enum CodeVerificationErrorType {
  ExpiredCodeException = 'ExpiredCodeException',
  MismatchCodeException = 'MismatchCodeException',
  UnknownError = 'UnknownError',
  NoError = 'NoError',
}

export enum GetVerificationCodeError {
  LimitExceededException = 'LimitExceededException',
}

export type IImpersonateUserResponse = ImpersonateUserResponse;

export type IProcessApprovalRequest = ProcessApprovalRequest;
export type IApprovalResponse = ApprovalResponse;
export { ApprovalType };

export type IPolicyRules = {
  name: string;
  defaultValue: string;
  currentValue: string;
  missedSaving?: string;
};

export interface IHardApprovalParams {
  travelerName: string;
  approverName: string;
  // similar to fully refundable deadline used in case of soft approval
  voidDate: string;
  tripId: string;
  pnrId: string;
  tripName: string;
  approvalId: string;
  outOfPolicy: string;
  violatedPolicyList?: IPolicyRules[];
  oopReasonCode?: string;
  oopReasonDescription?: string;
  approvalType: string;
  // date and time till which booking can be denied in case of soft approval
  approvalDeadline: string;
  // date and time till which booking can be cancelled/denied for free
  fullyRefundableDeadline?: string;
}

export const alreadyProcessedErrorCode = 'APPROVAL_INFO_ALREADY_PROCESSED';
export const voidWindowExpiredErrorCode = 'APPROVAL_REQUEST_TIMED_OUT';

export interface SSOOptionInfo {
  label: string;
  resourceKey: AuthProviderType;
}
